import type { IFilters } from '@/components/filters';
import { fetchNinjaCsrf, getCachedNinjaCsrf } from '@/libs/ninja/csrf';
import { BACKEND_SERVER_NINJA } from '@/settings';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { skeletonTabOnlyAds, skeletonTabsSeoAndAds } from '../skeleton-data';
import type {
  ICommonChartCard,
  ILineItemData,
  ILineProgressMap,
  IResponseAllAndAdsData,
  ISoiScatterItem,
  ISopScatterItem,
} from '../types';
import { ChartsTypeEnum } from '../types';

export const fetchAndSetDataAllTime = async (
  cardsAllTime: ICommonChartCard,
  filters: IFilters,
  abortControllers: AbortController[]
) => {
  const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
  const [
    {
      data: {
        data: { all: allTimeSoiNoAds = [], ads: allTimeSoiAds = [] },
      },
    },
    {
      data: {
        data: {
          all: allTimeSosNoAds = [],
          ads: allTimeSosAds = [],
          brands: brands,
        },
      },
    },
  ] = await Promise.all([
    axios.post<AxiosResponse<IResponseAllAndAdsData<ISoiScatterItem[]>>>(
      `${BACKEND_SERVER_NINJA}/reports/main_page/soi/report_soi`,
      filters,
      {
        withCredentials: true,
        headers: { 'X-CSRFTOKEN': csrf },
        signal: abortControllers[0]?.signal,
      }
    ),
    axios.post<AxiosResponse<IResponseAllAndAdsData<ISopScatterItem[]>>>(
      `${BACKEND_SERVER_NINJA}/reports/main_page/sos/report_sos`,
      { ...filters, with_brands: true },
      {
        withCredentials: true,
        headers: { 'X-CSRFTOKEN': csrf },
        signal: abortControllers[1]?.signal,
      }
    ),
  ]);

  const normalizeSopScatterData = (data: ISopScatterItem[]) => {
    const positions = data.map(({ positions }) => positions);
    const maxPositions = Math.max(...positions);
    const denominator = maxPositions / 20;

    return data.map((sopScatterItem) => ({
      ...sopScatterItem,
      squareValue: sopScatterItem.positions / denominator,
    }));
  };

  const normalizeSoiScatterData = (data: ISoiScatterItem[]) => {
    const products = data.map(({ products }) => products);
    const maxProducts = Math.max(...products);
    const denominator = maxProducts / 20;

    return data.map((soiScatterItem) => ({
      ...soiScatterItem,
      squareValue: soiScatterItem.products / denominator,
    }));
  };

  const AllTimeTabsAds = [
    { ...skeletonTabOnlyAds?.[0], data: allTimeSosAds },
    { ...skeletonTabOnlyAds?.[1], data: allTimeSoiAds },
  ];

  const AllTimeTabsNoAds = [
    {
      ...skeletonTabsSeoAndAds[0],
      data: normalizeSopScatterData(allTimeSosNoAds),
    },
    {
      ...skeletonTabsSeoAndAds[1],
      data: normalizeSoiScatterData(allTimeSoiNoAds),
    },
  ];

  return {
    cards: cardsAllTime.map((card) => ({
      ...card,
      tabs:
        card.type === ChartsTypeEnum.scatter
          ? AllTimeTabsNoAds
          : AllTimeTabsAds,
    })),
    uniqueBrands: brands,
  };
};

export const fetchAndSetDataProgress = async (
  cardsProgress: ICommonChartCard,
  filters: IFilters,
  abortControllers: AbortController[]
) => {
  const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
  const [
    {
      data: {
        data: { all: daysProgressAll, ads: daysProgressAds },
      },
    },
    {
      data: {
        data: { all: weeksProgressAll, ads: weeksProgressAds },
      },
    },
    {
      data: {
        data: { all: monthProgressAll, ads: monthProgressAds },
      },
    },
  ] = await Promise.all([
    axios.post<AxiosResponse<IResponseAllAndAdsData<ILineItemData[]>>>(
      `${BACKEND_SERVER_NINJA}/reports/main_page/by_period/report_by_period`,
      { ...filters, split: 'days' },
      {
        withCredentials: true,
        headers: { 'X-CSRFTOKEN': csrf },
        signal: abortControllers[2]?.signal,
      }
    ),
    axios.post<AxiosResponse<IResponseAllAndAdsData<ILineItemData[]>>>(
      `${BACKEND_SERVER_NINJA}/reports/main_page/by_period/report_by_period`,
      { ...filters, split: 'weeks' },
      {
        withCredentials: true,
        headers: { 'X-CSRFTOKEN': csrf },
        signal: abortControllers[3]?.signal,
      }
    ),
    axios.post<AxiosResponse<IResponseAllAndAdsData<ILineItemData[]>>>(
      `${BACKEND_SERVER_NINJA}/reports/main_page/by_period/report_by_period`,
      { ...filters, split: 'months' },
      {
        withCredentials: true,
        headers: { 'X-CSRFTOKEN': csrf },
        signal: abortControllers[4]?.signal,
      }
    ),
  ]);

  const cardsProgressMap: ILineProgressMap = {
    [ChartsTypeEnum.line]: [
      { title: 'По дням', data: daysProgressAll },
      { title: 'По неделям', data: weeksProgressAll },
      { title: 'По месяцам', data: monthProgressAll },
    ],
    [ChartsTypeEnum.barStack]: [
      { title: 'По дням', data: daysProgressAds },
      { title: 'По неделям', data: weeksProgressAds },
      { title: 'По месяцам', data: monthProgressAds },
    ],
  };

  return {
    cards: cardsProgress.map((card) => ({
      ...card,
      data: cardsProgressMap[card.type],
    })),
  };
};
